import clsx from "clsx";

interface Props {
  label: string;
  value?: number;
  onChange: (year: number) => void;
  error?: string;
  hint?: string;
  min?: number;
  max?: number;
}

/** Gruppen für Optgroup-Labels (neueste zuerst) */
function buildGroups(min: number, max: number): { label: string; years: number[] }[] {
  const groups: { label: string; years: number[] }[] = [];

  const addGroup = (label: string, from: number, to: number) => {
    const years: number[] = [];
    for (let y = Math.min(to, max); y >= Math.max(from, min); y--) years.push(y);
    if (years.length > 0) groups.push({ label, years });
  };

  addGroup(`${Math.floor(max / 10) * 10}–${max}`, Math.floor(max / 10) * 10, max);
  for (let decade = Math.floor(max / 10) * 10 - 10; decade >= Math.ceil(min / 10) * 10; decade -= 10) {
    addGroup(`${decade}er`, decade, decade + 9);
  }
  // Alles vor dem ersten vollen Jahrzehnt
  const firstDecade = Math.ceil(min / 10) * 10;
  if (min < firstDecade) {
    addGroup(`vor ${firstDecade}`, min, firstDecade - 1);
  }

  return groups;
}

export default function YearSelect({ label, value, onChange, error, hint, min = 1900, max = 2026 }: Props) {
  const id = `year-select-${label.toLowerCase().replace(/\s+/g, "-")}`;
  const groups = buildGroups(min, max);

  return (
    <div className="mb-4">
      <label htmlFor={id} className={clsx("block text-sm font-medium mb-1", error ? "text-neko-red" : "text-neko-gray-light")}>
        {label}
      </label>
      <select
        id={id}
        value={value ?? ""}
        onChange={(e) => {
          const v = parseInt(e.target.value);
          if (!isNaN(v)) onChange(v);
        }}
        data-testid="baujahr-select"
        aria-invalid={error ? "true" : undefined}
        aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
        className={clsx(
          "w-full border rounded-lg px-3 py-2.5 text-base bg-neko-dark-2",
          "focus:outline-none focus:ring-2 focus:ring-neko-orange/40 focus:border-neko-orange transition-all",
          "appearance-none cursor-pointer",
          error
            ? "border-neko-red text-neko-white"
            : "border-neko-dark-3 text-neko-white"
        )}
      >
        <option value="" disabled>Baujahr wählen</option>
        {groups.map((g) => (
          <optgroup key={g.label} label={g.label}>
            {g.years.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </optgroup>
        ))}
      </select>
      {hint && !error && (
        <p id={`${id}-hint`} className="text-xs text-neko-gray mt-1">{hint}</p>
      )}
      {error && (
        <p id={`${id}-error`} className="text-xs text-neko-red mt-1" role="alert">{error}</p>
      )}
    </div>
  );
}
